<script setup>
    import {computed, ref, watch} from "vue";
    import axios from "axios";
    import {onMounted} from "vue";
    import TaskItem from "./taskItem.vue";
    import SlideoutTask from "./slideoutTask.vue";
    import BaseEmail from "./baseEmail.vue";
    import BaseMessage from "./baseMessage.vue";

    const emit = defineEmits(['markedSent'])

    const loading = ref(true);
    const tasks = ref([]);
    const currentEmailsPage = ref(1);
    const currentLinkedinPage = ref(1);
    const pageSize = 8;
    const editingEmail = ref(false)
    const editingMessage = ref(false)
    const taskBeingManaged = ref(null)
    const context = ref('email')

    const setContext = (ctx) => {
        context.value = ctx;
    }

    const fetchTasks = async () => {
        try {
            const response = await axios.get(route('assistant.dailyTasks'));
            tasks.value = response.data;
            loading.value = false
        } catch (error) {
        }
    };

    const updateEmailContent = async () => {
        await axios.post(route('assistant.updateBaseEmail'), {
            subject: tasks.value.emailSubject,
            content: tasks.value.emailBody
        });
    }

    const updateMessageContent = async () => {
        await axios.post(route('assistant.updateBaseMessage'), {
            messageBody: tasks.value.messageBody,
        });
    }

    const action = async () => {
        try {
            const response = await axios.get(route('assistant.dailyTasks'));
            tasks.value = response.data;
        } catch (error) {
        }
    };

    const paginatedEmailTasks = computed(() => {
        if (! tasks.value.emailTasks) {
            return [];
        }

        let from = (currentEmailsPage.value - 1) * pageSize;
        let to = currentEmailsPage.value * pageSize;

        return Object.values(tasks.value.emailTasks).slice(from, to);
    })

    const emailPageCount = computed(() => {
        if (! tasks.value.emailTasks) {
            return 0;
        }

        return Math.ceil(Object.values(tasks.value.emailTasks).length / pageSize);
    });

    const paginatedLinkedinTasks = computed(() => {
        if (! tasks.value.linkedinTasks) {
            return [];
        }

        let from = (currentLinkedinPage.value - 1) * pageSize;
        let to = currentLinkedinPage.value * pageSize;

        return Object.values(tasks.value.linkedinTasks).slice(from, to);
    })

    const linkedinPageCount = computed(() => {
        if (! tasks.value.linkedinTasks) {
            return 0;
        }

        return Math.ceil(Object.values(tasks.value.linkedinTasks).length / pageSize);
    });

    const stripHtml = (html) => {
        const temp = document.createElement('div');
        temp.innerHTML = html;
        return temp.textContent || temp.innerText || '';
    };

    const openEmail = (task) => {
        const subject = encodeURIComponent(tasks.value.emailSubject);
        const plainBody = stripHtml(tasks.value.emailBody);
        const body = encodeURIComponent(plainBody);
        const to = encodeURIComponent(task.email);

        const mailtoLink = `mailto:${to}?subject=${subject}&body=${body}`;
        window.open(mailtoLink, '_blank');
    };

    const openInSlideout = (task) => {
        taskBeingManaged.value = task
    }

    const markTaskSent = async (task) => {
        taskBeingManaged.value = null
        const res = await axios.post(route('assistant.markAsSent'), {
            list_lead_id: task.id
        });

        if (res.data.success) {
            task.done = true
            emit('markedSent', task)
        }
    }

    const markTaskSkipped = async (task) => {
        await axios.post(route('assistant.markAsSkipped'), {
            list_lead_id: task.id
        });
        task.done = true
    }

    watch(editingEmail, (newValue) => {
        if (newValue === false) {
            updateEmailContent()
        }
    })

    watch(editingMessage, (newValue) => {
        if (newValue === false) {
            updateMessageContent()
        }
    })

    onMounted(() => {
        fetchTasks();
    });

</script>


<template>
    <div>
        <div class="grid-cols-2 gap-8 md:grid">
            <div class="col-span-2">
                <card-tabbed
                    @changestatustab="setContext"
                >
                    <card-tabbed-tab
                        title="Email"
                        value="email"
                        :active="true"
                    >
                        <h2 class="mb-4">New contacts to email</h2>
                        <div>
                            <div
                                v-if="loading"
                                class="flex justify-center"
                            >
                                <mf-spinner-medium
                                    class="w-12 h-6"
                                />
                            </div>
                            <ul class="divide-y">
                                <li
                                    v-if="! loading && tasks.emailTasks.length === 0 && tasks.processedEmailTasks === 0"
                                    class="py-3 text-center text-gray-500"
                                >
                                    We're currently gathering leads for you, check back soon.
                                </li>
                                <li
                                    v-if="! loading && tasks.emailTasks.length === 0 && tasks.processedEmailTasks > 0"
                                    class="py-3 text-center text-gray-500"
                                >
                                    You're all done for now, good job!
                                </li>
                                <li
                                    v-for="task in tasks.emailTasks"
                                    :key="task.name"
                                >
                                    <task-item
                                        :task="task"
                                        @mark-task-sent="markTaskSent"
                                        @mark-task-skipped="markTaskSkipped"
                                        @openEmail="openEmail"
                                        @openInSlideout="openInSlideout"
                                    />
                                </li>
                            </ul>

                            <div v-if="false">
                                <div class="flex justify-center mt-4">
                                    <div class="flex items-center space-x-2">
                                        <button-base
                                            size="sm"
                                            :style-type="currentEmailsPage > 1 ? 'secondary' : 'disabled'"
                                            @click="currentEmailsPage = currentEmailsPage > 1 ? currentEmailsPage - 1 : currentEmailsPage"
                                        >
                                            <i class="fa-light fa-chevron-left"></i>
                                        </button-base>
                                        <div class="text-xs text-center text-gray-400 min-w-20">
                                            {{ pageSize*(currentEmailsPage-1)+1 }}-{{ pageSize*currentEmailsPage }} of {{ pageSize*emailPageCount }}
                                        </div>
                                        <button-base
                                            size="sm"
                                            :style-type="emailPageCount > currentEmailsPage ? 'secondary' : 'disabled'"
                                            @click="currentEmailsPage = emailPageCount > currentEmailsPage ? currentEmailsPage + 1 : currentEmailsPage"
                                        >
                                            <i class="fa-light fa-chevron-right"></i>
                                        </button-base>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </card-tabbed-tab>
                    <card-tabbed-tab
                        title="LinkedIn"
                        value="linkedin"
                    >
                        <h2>Contacts to nudge on LinkedIn</h2>
                        <div>
                            <div
                                v-if="loading"
                                class="flex justify-center"
                            >
                                <mf-spinner-medium
                                    class="w-12 h-6"
                                />
                            </div>
                            <ul class="divide-y">
                                <li
                                    v-if="! loading && tasks.linkedinTasks.length === 0 && tasks.processedLinkedinTasks === 0"
                                    class="py-3 text-center text-gray-500"
                                >
                                    No contacts to nudge right now, check back in a day or two
                                </li>
                                <li
                                    v-if="! loading && tasks.linkedinTasks.length === 0 && tasks.processedLinkedinTasks > 0"
                                    class="py-3 text-center text-gray-500"
                                >
                                    You're all done for now, good job!
                                </li>
                                <li
                                    v-for="task in tasks.linkedinTasks"
                                    :key="task.name"
                                >
                                    <task-item
                                        :task="task"
                                        @mark-task-sent="markTaskSent"
                                        @mark-task-skipped="markTaskSkipped"
                                        @openEmail="openEmail"
                                        @openInSlideout="openInSlideout"
                                    />
                                </li>
                            </ul>

                            <div v-if="false">
                                <div class="flex justify-center mt-4">
                                    <div class="flex items-center space-x-2">
                                        <button-base
                                            size="sm"
                                            :style-type="currentLinkedinPage > 1 ? 'secondary' : 'disabled'"
                                            @click="currentLinkedinPage = currentLinkedinPage > 1 ? currentLinkedinPage - 1 : currentLinkedinPage"
                                        >
                                            <i class="fa-light fa-chevron-left"></i>
                                        </button-base>
                                        <div class="text-xs text-center text-gray-400 min-w-20">
                                            {{ pageSize*(currentLinkedinPage-1)+1 }}-{{ pageSize*currentLinkedinPage }} of {{ pageSize*linkedinPageCount }}
                                        </div>
                                        <button-base
                                            size="sm"
                                            :style-type="linkedinPageCount > currentLinkedinPage ? 'secondary' : 'disabled'"
                                            @click="currentLinkedinPage = linkedinPageCount > currentLinkedinPage ? currentLinkedinPage + 1 : currentLinkedinPage"
                                        >
                                            <i class="fa-light fa-chevron-right"></i>
                                        </button-base>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </card-tabbed-tab>

                    <card-tabbed-tab
                        title="Messages"
                        value="messages"
                    >
                        <div>
                            <BaseEmail
                                v-if="tasks.emailSubject && tasks.emailBody"
                                class="mb-8"
                                :subject="tasks.emailSubject"
                                :body="tasks.emailBody"
                                @update="fetchTasks()"
                            />

                            <BaseMessage
                                v-if="tasks.messageBody"
                                :body="tasks.messageBody"
                            />
                        </div>
                    </card-tabbed-tab>

                </card-tabbed>
            </div>
        </div>
        <slideout-with-slots
            v-if="taskBeingManaged"
            @close="taskBeingManaged = null"
        >
            <template #title>
                <h3 class="text-lg font-bold">Task details</h3>
            </template>
            <template #body>
                <SlideoutTask
                    :task="taskBeingManaged"
                    :context="context"
                    :default-email-subject="tasks.emailSubject"
                    :default-email-body="tasks.emailBody"
                    :default-message="tasks.messageBody"
                    @mark-task-sent="markTaskSent"
                />
            </template>
        </slideout-with-slots>
    </div>
</template>

<style scoped>
</style>
